

import { useState, useCallback } from "react";
import { useAppSelector } from "@/OneLiners";
import { ENIGMA_MAX_DIGITS } from "../challenge-constants";

/**
 * Hook: usePinEntryChallenge
 *
 * Description:
 * Custom hook to manage a virtual PIN or enigma digit entry system.
 * Provides utilities to handle digit-based input with a maximum length.
 *
 * Responsibilities include:
 * - Tracking digits entered by the user
 * - Enforcing a maximum length
 * - Managing positions of digits entered
 * - Resetting the input when necessary
 *
 * Typical use case: virtual keypad input for challenges where validation
 * occurs only after a full sequence is entered (e.g., 4 digits).
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 *
 * @param maxLength - Maximum number of digits allowed (default: ENIGMA_MAX_DIGITS)
 */
export function usePinEntryChallenge(maxLength = ENIGMA_MAX_DIGITS) {

    /**
     * =====================================================
     * Redux state and local state
     * =====================================================
     *
     * - userChallenge: fetches current challenge from session state
     * - digits: current string of digits entered
     * - positions: string tracking the positions of entered digits
     * - enigmas: array of expected digits for the challenge
     */
    const userChallenge = useAppSelector(state => state.sessionState.userChallenge);
    const [digits, setDigits] = useState<string>("");
    const [positions, setPositions] = useState<string>("");
    const enigmas = (userChallenge?.enigmaDigits?.split('') || []) as string[];

     /**
     * =====================================================
     * Digit input handler
     * =====================================================
     *
     * addDigit:
     * - Adds a digit at the specified position to the current entry
     * - Respects the maxLength constraint
     * - Updates both digits and their positions
     *
     * @param position - Index of the digit to append from the challenge array
     */
    const addDigit = useCallback((position: number) => {
        setDigits(currentDigits => {
            if (currentDigits.length >= maxLength) return currentDigits;
            return currentDigits + enigmas[position];
        });

        setPositions(currentPositions => {
            if (currentPositions.length >= maxLength) return currentPositions;
            return currentPositions + position;
        });
    }, [maxLength, enigmas]);

     /**
     * =====================================================
     * Reset function
     * =====================================================
     *
     * clearDigits:
     * - Resets the digits and positions to empty strings
     * - Useful for clearing the input after submission or cancellation
     */
    const clearDigits = useCallback(() => {
        setDigits("")
        setPositions("");
    }, []);


    /**
     * =====================================================
     * Hook API
     * =====================================================
     *
     * Returns an object containing:
     * - enigmas: array of expected digits
     * - positions: current positions entered
     * - digits: current string of digits entered
     * - addDigit: function to append a digit
     * - clearDigits: function to reset entry
     * - isFullfilled: boolean indicating if entry has reached maxLength
     */
    return {
        enigmas,
        positions,
        digits,
        addDigit,
        clearDigits,
        isFullfilled: digits.length === maxLength
    };
}
